/* ─────────────────────────────────────────────────────────────────
   Access Management — single source of truth.
   Sidebar visibility, route guards, and the AccessManagementTab UI
   all derive from these tables.
   ───────────────────────────────────────────────────────────────── */


export interface PageDef {
  path: string;
  label: string;
}

export interface GroupDef {
  title: string;
  pages: PageDef[];
}

export interface AuthorityDef {
  key: string;
  label: string;
  description?: string;
}

/* ── Pages, organised by sidebar group ─────────────────────────── */
export const PAGE_GROUPS: GroupDef[] = [
  {
    title: "Main",
    pages: [
      { path: "/admin-dashboard", label: "Dashboard" },
      { path: "/users", label: "Users" },
      { path: "/reports", label: "User Reports" },
      { path: "/kyc", label: "User KYC" },
      { path: "/wallet", label: "Wallet" },
      { path: "/affiliate", label: "Invite / Affiliate" },
      { path: "/redeemcode", label: "Redeem Codes" },
      { path: "/turnover-report", label: "Turnover Report" },
    ],
  },
  {
    title: "Agent Panel",
    pages: [
      { path: "/admin-dashboard", label: "Dashboard" },
      { path: "/users", label: "Users" },
    ],
  },
  {
    title: "Admin",
    pages: [
      { path: "/admin-management", label: "Client Management" },
      { path: "/percentage-hierarchy", label: "Percentage Hierarchy" },
    ],
  },
  {
    title: "Tier & Branch",
    pages: [
      { path: "/tier-management", label: "Tier Management" },
      { path: "/branch-management", label: "Branch Management" },
      { path: "/banking", label: "Banking" },
    ],
  },
  {
    title: "History",
    pages: [
      { path: "/history", label: "Bet History" },
    ],
  },
  // Sports Exchange / Sports Analytics nav groups removed — sports hidden everywhere.
  {
    title: "Cash Flow",
    pages: [
      { path: "/vaultpro", label: "Vault Pro" },
    ],
  },
  {
    title: "Gaming",
    pages: [
      { path: "/providers-priority", label: "Providers Priority" },
      { path: "/vendor-priority", label: "Vendor Priority" },
      { path: "/type-priority", label: "Type Priority" },
      { path: "/provider-onoff", label: "Provider On / Off" },
      { path: "/games-typewise", label: "Type-wise Games" },
      { path: "/games-edit", label: "Edit Games" },
    ],
  },
  {
    title: "Original Games",
    pages: [
      { path: "/original-games", label: "Dashboard" },
      { path: "/original-games/bets", label: "Bet History" },
      { path: "/original-games/sessions", label: "Sessions" },
      { path: "/original-games/games", label: "Games" },
      { path: "/original-games/callbacks", label: "Callback Log" },
      { path: "/original-games/validation", label: "Validation" },
    ],
  },
  {
    title: "Transactions",
    pages: [
      { path: "/deposit", label: "Deposit" },
      { path: "/withdraw", label: "Withdraw" },
      { path: "/account-statement", label: "Account Statement" },
    ],
  },
  {
    title: "Marketing",
    pages: [
      { path: "/giftcard-admin", label: "Gift Cards" },
      { path: "/spinwheel", label: "Spin Wheel" },
      { path: "/challenges", label: "Challenges" },
      { path: "/promotion", label: "Promotions" },
      { path: "/blog", label: "Blog" },
      { path: "/seo-manager", label: "SEO Manager" },
    ],
  },
  {
    // Mirrors the sidebar's own "Banner Management" group — the title must match
    // exactly or groupVisible() hides it from executives no matter what is granted.
    title: "Banner Management",
    pages: [
      { path: "/banner", label: "Banners" },
    ],
  },
  {
    title: "Settings",
    pages: [
      { path: "/bankdetails", label: "Bank Details" },
      { path: "/siteconfig", label: "Site Config" },
      // Gaming routes moved to the "Gaming" nav group.
      { path: "/userconfig", label: "User Config" },
      { path: "/settings", label: "General Settings" },
    ],
  },
  {
    title: "Services",
    pages: [
      { path: "/notification", label: "Notification" },
      { path: "/notification-settings", label: "Notification Settings" },
    ],
  },
];

/* ── Action-level authority toggles ────────────────────────────── */
export const AUTHORITY_GROUPS: { title: string; items: AuthorityDef[] }[] = [
  {
    title: "User Management",
    items: [
      { key: "canCreateUser",        label: "Create User" },
      { key: "canCreateAgent",       label: "Create Agent / Staff" },
      { key: "canEditUser",          label: "Edit User" },
      { key: "canDeleteUser",        label: "Delete User" },
      { key: "canChangeUserPassword",label: "Reset User Password" },
      { key: "canLockUser",          label: "Lock / Unlock User" },
      { key: "canChangeCreditLimit", label: "Change Credit Limit" },
      { key: "canChangeExposureLimit", label: "Change Exposure Limit" },
      { key: "canManageKYC",         label: "Manage KYC" },
    ],
  },
  {
    title: "Transactions",
    items: [
      { key: "canTransferCredit",    label: "G/T Credit Transfer" },
      { key: "canTransferCasino",    label: "Casino G/T Transfer" },
      { key: "canApproveDeposit",    label: "Approve / Reject Deposit" },
      { key: "canApproveWithdraw",   label: "Approve / Reject Withdraw" },
    ],
  },
  {
    title: "Settlement",
    items: [
      { key: "canSettleSports",      label: "Sports / Market Settlement" },
      { key: "canSettleFancy",       label: "Fancy Settlement" },
      { key: "canSettleCasino",      label: "Casino Settlement" },
    ],
  },
  {
    title: "Marketing",
    items: [
      { key: "canCreateRedeemCode",  label: "Create Redeem Code" },
      { key: "canIssueGiftCard",     label: "Issue Gift Card" },
      { key: "canIssueBonus",        label: "Issue Bonus" },
      { key: "canSendNotification",  label: "Send Notification" },
    ],
  },
  {
    title: "Tier & Branch",
    items: [
      { key: "canManageTiers",       label: "Manage Tiers" },
      { key: "canManageBranches",    label: "Manage Branches" },
      { key: "canRunTierAutoAssign", label: "Run Tier Auto-Assign", description: "Bulk-assign users to tiers based on their transaction metrics." },
      { key: "canManageBanking",     label: "Manage Banking", description: "Create bank accounts, pool them onto tiers, and create branch logins." },
    ],
  },
  {
    title: "Reports & Config",
    items: [
      { key: "canViewReports",       label: "View Reports" },
      { key: "canViewOriginalGames", label: "View Original Games", description: "Dashboard, bet history, sessions and callback log of the Original Games provider." },
      { key: "canExportCSV",         label: "Export Data (CSV)" },
      { key: "canEditSiteConfig",    label: "Edit Site Config" },
      { key: "canEditPriorities",    label: "Edit Providers / Vendors / Type Priority" },
    ],
  },
  {
    title: "Access Management",
    items: [
      {
        key: "canManageStaff",
        label: "Manage Executives",
        description: "Allows this user to open Access Management and create / edit / view activity for their executives.",
      },
    ],
  },
];

/* Flat list of all authority keys — handy for default state */
export const ALL_AUTHORITY_KEYS: string[] = AUTHORITY_GROUPS.flatMap(g =>
  g.items.map(i => i.key)
);

export const ALL_GROUP_TITLES: string[] = PAGE_GROUPS.map(g => g.title);

export const ALL_PAGE_PATHS: string[] = Array.from(
  new Set(PAGE_GROUPS.flatMap(g => g.pages.map(p => p.path)))
);

/* ── Permission shape stored per staff row ─────────────────────── */
export interface StaffPermissions {
  /** group title -> visible (overrides individual page when false) */
  groups: Record<string, boolean>;
  /** route path -> visible */
  pages: Record<string, boolean>;
  /** authority key -> granted */
  authority: Record<string, boolean>;
}

export const buildEmptyPermissions = (): StaffPermissions => ({
  groups: Object.fromEntries(ALL_GROUP_TITLES.map(t => [t, false])),
  pages: Object.fromEntries(ALL_PAGE_PATHS.map(p => [p, false])),
  authority: Object.fromEntries(ALL_AUTHORITY_KEYS.map(k => [k, false])),
});

export const buildFullPermissions = (): StaffPermissions => ({
  groups: Object.fromEntries(ALL_GROUP_TITLES.map(t => [t, true])),
  pages: Object.fromEntries(ALL_PAGE_PATHS.map(p => [p, true])),
  authority: Object.fromEntries(ALL_AUTHORITY_KEYS.map(k => [k, true])),
});

/* First page the holder of these permissions is actually allowed to open,
   in sidebar order. Used for the post-login landing redirect so a narrowly
   scoped executive is never dropped on a page they cannot see. */
export const firstVisiblePage = (p: StaffPermissions): string | null => {
  for (const group of PAGE_GROUPS) {
    for (const page of group.pages) {
      if (p.pages?.[page.path]) return page.path;
    }
  }
  return null;
};

/* Agent-hierarchy roles short-circuit the permission system.
   They use the legacy GROUP_ACCESS whitelist in Sidebar.tsx and have full
   `can()` authority — their scope is constrained by role level (CHILD_ROLES)
   rather than per-key permission flags. Only "Executive" runs through
   usePermissions() / lordsApi.getMyPermissions(). */
export const SUPER_ROLES = [
  "SuperAdmin",
  "MotherAdmin",
  "Admin",
  "SubAdmin",
  "Master",
  "Agent",
  "SubAgent",
] as const;
